import {
  useEffect,
  useState,
  type ReactNode,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './AttributeSelectModal.module.css';
import { sortByRecommended, isRecommendedKey, OptionIcon, type HierarchicalOption, type RecommendedKeys } from './HierarchicalSelect';

interface AttributeSelectModalProps {
  label: string;
  options: HierarchicalOption[];
  value: string;
  onChange: (selectedKey: string) => void;
  isOpen: boolean;
  onClose: () => void;
  // リセット行のラベル(既定「指定なし」)。映像スタイル等で「デフォルト」に変えるため。
  noneLabel?: string;
  // この値を「既定(=変更なし)」とみなす。value===defaultValue ならリセット行を出さない。
  // 既定 'none'(従来挙動)。映像スタイルは 'photorealistic' を渡して実写風を既定扱いにする。
  defaultValue?: string;
  /** 商品AIプロファイル推奨 (計画書§5.2)。フラットなチップ列 (OptionChips) にのみ適用。
   *  グリッド/図表系ピッカー (placement_grid 等) は位置がmetadataで固定されるため対象外。 */
  recommendedKeys?: RecommendedKeys;
  /** 選択肢ごとにラベルの頭へ出すアイコン (HierarchicalSelect の optionIcon をそのまま受け取る)。 */
  optionIcon?: (option: HierarchicalOption) => string | undefined;
}

function getMeta(opt: HierarchicalOption): Record<string, unknown> {
  return (opt.metadata as Record<string, unknown> | null | undefined) ?? {};
}

function metaString(opt: HierarchicalOption, key: string): string | undefined {
  const v = getMeta(opt)[key];
  return typeof v === 'string' ? v : undefined;
}

/** 「正面（補助）」のような括弧書きを { main, sub } に分割（全角/半角括弧対応） */
function splitParenLabel(label: string): { main: string; sub: string } {
  const m = label.match(/^(.*?)[（(]([^（）()]*)[）)]\s*$/);
  if (m && m[1].trim()) return { main: m[1].trim(), sub: m[2].trim() };
  return { main: label, sub: '' };
}

/** 3×3 グリッドのセル並び（左上→右下） */
const GRID_CELLS = [
  'top_left', 'top', 'top_right',
  'left', 'center', 'right',
  'bottom_left', 'bottom', 'bottom_right',
] as const;
type GridCell = (typeof GRID_CELLS)[number];

/** カメラアイコン(camera_video.svg)はデフォルトでレンズが右向き(→)。
   各セルからレンズが中心(被写体)へ向くよう回転角(deg)を定義する。 */
const CELL_CAMERA_ROTATION: Record<GridCell, number> = {
  left: 0,
  right: 180,
  top: 90,
  bottom: -90,
  top_left: 45,
  top_right: 135,
  bottom_left: -45,
  bottom_right: -135,
  center: 0,
};

/** camera_angle: metadata.angle → グリッドセル。未マップの angle は補助ボタン送り */
const ANGLE_TO_CELL: Record<string, GridCell> = {
  iso: 'top_left',
  top: 'top',
  high: 'top_right',
  left: 'left',
  right: 'right',
  low: 'bottom',
  front: 'center',
};

/**
 * 配置グリッド（picker: "placement_grid"）。
 * options の metadata.cell でセル位置を決め、3×3 のどこに被写体を置くかを選ばせる。
 * cell を持たない option（クローズアップ / ランダム等）は補助ボタンとして下に並べる。
 */
function PlacementGrid({
  options,
  value,
  onSelect,
}: {
  options: HierarchicalOption[];
  value: string;
  onSelect: (key: string) => void;
}) {
  const byCell = new Map<string, HierarchicalOption>();
  const aux: HierarchicalOption[] = [];
  for (const opt of options) {
    const cell = metaString(opt, 'cell');
    if (cell && (GRID_CELLS as readonly string[]).includes(cell)) {
      byCell.set(cell, opt);
    } else {
      aux.push(opt);
    }
  }
  const selectedCell = options.find((o) => o.key === value && metaString(o, 'cell'));

  return (
    <div className={styles.pickerWrap}>
      <div className={`${styles.grid} ${styles.gridPlacement}`} role="group" aria-label="被写体の配置">
        {GRID_CELLS.map((cell) => {
          const opt = byCell.get(cell);
          const isSelected = !!opt && opt.key === value;
          if (!opt) {
            return (
              <span
                key={cell}
                className={`${styles.gridCell} ${styles.gridCellEmpty}`}
                aria-hidden="true"
              />
            );
          }
          const { main, sub } = splitParenLabel(opt.label);
          return (
            <button
              key={cell}
              type="button"
              className={`${styles.gridCell} ${styles.angleCell} ${isSelected ? styles.gridCellSelected : ''}`}
              onClick={() => onSelect(opt.key)}
              aria-pressed={isSelected}
              title={opt.label}
            >
              <img src="/water_bottle_glass.svg" alt="" aria-hidden="true" className={styles.placementIcon} />
              <span className={styles.angleLabel}>
                {sub && <span className={styles.angleLabelSub}>{sub}</span>}
                <span>{main}</span>
              </span>
            </button>
          );
        })}
      </div>
      {selectedCell && <p className={styles.pickerSelectedLabel}>{selectedCell.label}</p>}
      {aux.length > 0 && (
        <div className={styles.auxRow}>
          {aux.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`${styles.auxButton} ${value === opt.key ? styles.auxButtonSelected : ''}`}
              onClick={() => onSelect(opt.key)}
              aria-pressed={value === opt.key}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * アングルピッカー（picker: "camera_angle"）。
 * 中央の被写体の周囲に、metadata.angle に応じて方向チップを配置する。
 * 位置にマップされない angle（正面 / 後ろ姿 / 斜め等）は補助ボタンとして下に並べる。
 */
function CameraAnglePicker({
  options,
  value,
  onSelect,
}: {
  options: HierarchicalOption[];
  value: string;
  onSelect: (key: string) => void;
}) {
  const byCell = new Map<GridCell, HierarchicalOption>();
  const aux: HierarchicalOption[] = [];
  for (const opt of options) {
    const angle = metaString(opt, 'angle');
    const cell = angle ? ANGLE_TO_CELL[angle] : undefined;
    if (cell) {
      byCell.set(cell, opt);
    } else {
      aux.push(opt);
    }
  }

  return (
    <div className={styles.pickerWrap}>
      <div className={styles.grid} role="group" aria-label="カメラアングル">
        {GRID_CELLS.map((cell) => {
          const opt = byCell.get(cell);
          // 中央セルは「正面(front)」用。option があればクリック可、無ければ被写体マーカーを表示。
          if (cell === 'center' && !opt) {
            return (
              <span key={cell} className={`${styles.gridCell} ${styles.gridSubject}`} aria-hidden="true">
                <span className={styles.gridSubjectIcon}>🧍</span>
                <span className={styles.gridSubjectLabel}>被写体</span>
              </span>
            );
          }
          if (!opt) {
            return <span key={cell} className={`${styles.gridCell} ${styles.gridCellEmpty}`} aria-hidden="true" />;
          }
          const isSelected = opt.key === value;
          const isCenter = cell === 'center';
          return (
            <button
              key={cell}
              type="button"
              className={`${styles.gridCell} ${styles.angleCell} ${isSelected ? styles.gridCellSelected : ''}`}
              onClick={() => onSelect(opt.key)}
              aria-pressed={isSelected}
              title={opt.label}
            >
              <img
                src={isCenter ? '/camera_bootstrap.svg' : '/camera_video.svg'}
                alt=""
                aria-hidden="true"
                className={styles.cameraIcon}
                style={isCenter ? undefined : { transform: `rotate(${CELL_CAMERA_ROTATION[cell]}deg)` }}
              />
              {(() => {
                const { main, sub } = splitParenLabel(opt.label);
                return (
                  <span className={styles.angleLabel}>
                    {sub && <span className={styles.angleLabelSub}>{sub}</span>}
                    <span>{main}</span>
                  </span>
                );
              })()}
            </button>
          );
        })}
      </div>
      {aux.length > 0 && (
        <div className={styles.auxRow}>
          {aux.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`${styles.auxButton} ${value === opt.key ? styles.auxButtonSelected : ''}`}
              onClick={() => onSelect(opt.key)}
              aria-pressed={value === opt.key}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * shot_size: 被写体サイズ（撮影サイズ）の縦フレーミング図。
 * 人物シルエットに、各 option の metadata.frame に応じた写真フレーム(枠)を重ね、
 * 選択 or ホバー中はフレーム外の被写体をグレーアウトして撮影範囲を示す。
 * 寄りのサイズ(より寄り/顔)はフレームの上端を下げ、幅も狭めてクローズアップを表現する。
 * frame を持たない option(下半身など)は補助ボタンとして図の下に並べる。
 */
// 各サイズのフレーム定義。frac=切り取り線(フレーム下端)、top=フレーム上端、w=フレーム幅。
// いずれもシルエット縦軸(bodyTop..feet)/横軸(中心 SHOT_FRAME_CX)に対する viewBox 値。
// pad: 切り取り線(フレーム下端)より下に追加する余白(viewBox 単位)。全身の足元 footroom 用。
const SHOT_FRAMES: { key: string; frac: number; top: number; w: number; pad?: number }[] = [
  { key: 'eyes', frac: 0.09, top: 0.02, w: 16 }, // より寄り(ECU): 目元の狭いフレーム
  { key: 'face', frac: 0.17, top: 0.0, w: 34 }, // 顔のアップ(CU)
  { key: 'shoulder', frac: 0.24, top: 0.0, w: 44 }, // アップショット(US): 肩のあたり
  { key: 'bust', frac: 0.34, top: 0.0, w: 52 }, // バストショット(BS)
  { key: 'waist', frac: 0.5, top: 0.0, w: 62 }, // ウエストアップ(WS)
  { key: 'knee', frac: 0.78, top: 0.0, w: 66 }, // 膝上(KS)
  { key: 'full', frac: 1.0, top: 0.0, w: 66, pad: 7 }, // 全身(FF): 足元に少し余白
];
const SHOT_FRAME_BY_KEY: Record<string, (typeof SHOT_FRAMES)[number]> = Object.fromEntries(
  SHOT_FRAMES.map((f) => [f.key, f]),
);

// 縦軸(頭→下)に乗らない補助サイズ用の写真フレーム band(top..frac の帯)。
// 線としては描かず、補助ボタンのホバー/選択時にフレームだけをプレビューする。option の key で対応付け。
const SHOT_AUX_FRAMES: Record<string, { top: number; frac: number; w: number; pad?: number }> = {
  lower_body: { top: 0.5, frac: 1.0, w: 62, pad: 7 }, // 下半身: 腰〜足元(全身と同じ余白まで)を囲う
};

// viewBox 0 0 200 150 上のシルエット座標。bodyTop=8, feet=140 (高さ132)。
const SHOT_VB = { top: 8, height: 132 };
const shotYOf = (frac: number) => SHOT_VB.top + frac * SHOT_VB.height;

// 写真フレームの横中心、上端の最小マージン(top=0 用)。
const SHOT_FRAME_CX = 55;
const SHOT_FRAME_MIN_TOP = 3;

// 人物シルエット (public/person_icon_01.svg)。元 viewBox 0 0 124.189 132.243 を
// 頭頂≈y8 / 足元≈y140 / 中心x55 に合わせて配置。フレーム内/外の塗り分けで 2 回描画する。
const SHOT_BODY_TRANSFORM = 'translate(-16.23 -1.85) scale(1.1471)';
const SHOT_BODY_PATH =
  'm62.096 8.5859c-5.208 0-9.424 4.2191-9.424 9.4261 0.001 5.203 4.217 9.424 9.424 9.424 5.202 0 9.422-4.221 9.422-9.424 0-5.208-4.22-9.4261-9.422-9.4261zm-10.41 21.268c-6.672 0-12.131 5.407-12.131 12.07v29.23c0 2.275 1.791 4.123 4.07 4.123 2.28 0 4.127-1.846 4.127-4.123v-26.355h2.102s0.048 68.811 0.048 73.331c0 3.05 2.478 5.53 5.532 5.53 3.052 0 5.525-2.48 5.525-5.53v-42.581h2.27v42.581c0 3.05 2.473 5.53 5.531 5.53 3.054 0 5.549-2.48 5.549-5.53v-73.331h2.127v26.355c0 2.275 1.85 4.123 4.126 4.123 2.28 0 4.073-1.846 4.073-4.123v-29.23c0-6.663-5.463-12.07-12.129-12.07h-20.82z';
const SHOT_FRAME_CLIP_ID = 'shotFrameClip';

function ShotSizePicker({
  options,
  value,
  onSelect,
}: {
  options: HierarchicalOption[];
  value: string;
  onSelect: (key: string) => void;
}) {
  const [hoverKey, setHoverKey] = useState<string | null>(null);

  const byFrame = new Map<string, HierarchicalOption>();
  const aux: HierarchicalOption[] = [];
  for (const opt of options) {
    const frame = metaString(opt, 'frame');
    if (frame && SHOT_FRAME_BY_KEY[frame] != null) {
      byFrame.set(frame, opt);
    } else if (opt.key !== 'none') {
      // 指定なしはヘッダーのリセットで扱うため aux からは除外
      aux.push(opt);
    }
  }
  const rows = SHOT_FRAMES.filter((f) => byFrame.has(f.key)).map((f) => ({
    ...f,
    opt: byFrame.get(f.key) as HierarchicalOption,
  }));
  const selected = rows.find((r) => r.opt.key === value);
  // option key → フレーム geometry(縦軸の line frame か、補助の band frame)。
  const frameGeomOf = (key: string | null | undefined) => {
    if (!key) return null;
    const r = rows.find((rr) => rr.opt.key === key);
    if (r) return { top: r.top, frac: r.frac, w: r.w, pad: r.pad ?? 0 };
    const a = SHOT_AUX_FRAMES[key];
    return a ? { top: a.top, frac: a.frac, w: a.w, pad: a.pad ?? 0 } : null;
  };
  // フレームはホバー中を優先プレビュー(アクセント)。無ければ選択中(白太字)。
  const hoverGeom = frameGeomOf(hoverKey);
  const activeGeom = hoverGeom ?? frameGeomOf(value);

  const activate =
    (key: string) => (e: ReactKeyboardEvent<SVGGElement> | ReactMouseEvent<SVGGElement>) => {
      if ('key' in e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
      }
      onSelect(key);
    };

  // フレーム矩形(viewBox 座標)。シルエットの塗り分けクリップと枠線で共用。
  const fg = activeGeom
    ? (() => {
        const fw = activeGeom.w;
        const fx = SHOT_FRAME_CX - fw / 2;
        const topY = activeGeom.top > 0 ? shotYOf(activeGeom.top) : SHOT_FRAME_MIN_TOP;
        const frameBottom = shotYOf(activeGeom.frac) + activeGeom.pad;
        return { fx, fw, topY, frameBottom };
      })()
    : null;

  return (
    <div className={styles.pickerWrap}>
      <div className={styles.shotFigure}>
        <svg viewBox="0 0 200 150" className={styles.shotSvg} role="group" aria-label="被写体サイズ">
          {/* シルエットを 2 層で塗り分け: 全身を濃色(.shotBodyDim)で敷き、フレーム内だけ
              通常色(.shotBody)をクリップして上に重ねる。黒い矩形は重ねない。 */}
          {fg && (
            <defs>
              <clipPath id={SHOT_FRAME_CLIP_ID}>
                <rect
                  x={fg.fx}
                  y={fg.topY}
                  width={fg.fw}
                  height={fg.frameBottom - fg.topY}
                  rx="2"
                />
              </clipPath>
            </defs>
          )}
          <g
            className={fg ? styles.shotBodyDim : styles.shotBody}
            transform={SHOT_BODY_TRANSFORM}
          >
            <path d={SHOT_BODY_PATH} />
          </g>
          {fg && (
            <g clipPath={`url(#${SHOT_FRAME_CLIP_ID})`}>
              <g className={styles.shotBody} transform={SHOT_BODY_TRANSFORM}>
                <path d={SHOT_BODY_PATH} />
              </g>
            </g>
          )}
          {/* 写真フレームの枠線 */}
          {fg && (
            <rect
              className={`${styles.shotFrame} ${hoverGeom ? styles.shotFrameHover : ''}`}
              x={fg.fx}
              y={fg.topY}
              width={fg.fw}
              height={fg.frameBottom - fg.topY}
              rx="2"
            />
          )}
          {/* 各サイズの切り取り線＋ラベル(クリック領域) */}
          {rows.map((r) => {
            // pad があるサイズ(全身)は、点線もフレーム下端(cut + pad)まで下げる
            const y = shotYOf(r.frac) + (r.pad ?? 0);
            const isSel = r.opt.key === value;
            return (
              <g
                key={r.opt.id}
                className={`${styles.shotRowG} ${isSel ? styles.shotRowSelected : ''}`}
                role="button"
                tabIndex={0}
                aria-pressed={isSel}
                aria-label={r.opt.label}
                onClick={activate(r.opt.key)}
                onKeyDown={activate(r.opt.key)}
                onMouseEnter={() => setHoverKey(r.opt.key)}
                onMouseLeave={() => setHoverKey((k) => (k === r.opt.key ? null : k))}
                onFocus={() => setHoverKey(r.opt.key)}
                onBlur={() => setHoverKey((k) => (k === r.opt.key ? null : k))}
              >
                <rect className={styles.shotHit} x="8" y={y - 9} width="160" height="18" />
                <line className={styles.shotLine} x1="20" y1={y} x2="88" y2={y} />
                <text className={styles.shotLabel} x="94" y={y}>
                  {r.opt.label}
                </text>
              </g>
            );
          })}
        </svg>
      </div>
      {selected && <p className={styles.pickerSelectedLabel}>{selected.opt.label}</p>}
      {aux.length > 0 && (
        <div className={styles.auxRow}>
          {aux.map((opt) => {
            const hasFrame = SHOT_AUX_FRAMES[opt.key] != null;
            return (
              <button
                key={opt.id}
                type="button"
                className={`${styles.auxButton} ${value === opt.key ? styles.auxButtonSelected : ''}`}
                onClick={() => onSelect(opt.key)}
                aria-pressed={value === opt.key}
                onMouseEnter={hasFrame ? () => setHoverKey(opt.key) : undefined}
                onMouseLeave={
                  hasFrame ? () => setHoverKey((k) => (k === opt.key ? null : k)) : undefined
                }
                onFocus={hasFrame ? () => setHoverKey(opt.key) : undefined}
                onBlur={hasFrame ? () => setHoverKey((k) => (k === opt.key ? null : k)) : undefined}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** リーフ option のピル型 chip 列（テキストフィット） */
function OptionChips({
  options,
  value,
  onSelect,
  recommendedKeys,
  optionIcon,
}: {
  options: HierarchicalOption[];
  value: string;
  onSelect: (key: string) => void;
  recommendedKeys?: RecommendedKeys;
  optionIcon?: (option: HierarchicalOption) => string | undefined;
}) {
  const sortedOptions = sortByRecommended(options, recommendedKeys);
  return (
    <div className={styles.chips}>
      {sortedOptions.map((opt) => (
        <button
          key={opt.id}
          type="button"
          className={`${styles.chip} ${value === opt.key ? styles.chipSelected : ''} ${opt.is_sensitive ? styles.chipSensitive : ''}`}
          onClick={() => onSelect(opt.key)}
          aria-pressed={value === opt.key}
        >
          {(() => { const url = optionIcon?.(opt); return url ? <OptionIcon url={url} /> : null; })()}
          {opt.label}
          {isRecommendedKey(opt.key, recommendedKeys) && (
            <span className={styles.recommendedBadge}>おすすめ</span>
          )}
        </button>
      ))}
    </div>
  );
}

/** この group のサブツリーに選択中の値が含まれるか（ヘッダーのアクティブ表示用） */
function groupContainsValue(group: HierarchicalOption, value: string): boolean {
  for (const c of group.children ?? []) {
    if (c.key === value) return true;
    if (c.children && groupContainsValue(c, value)) return true;
  }
  return false;
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`${styles.sectionArrow} ${open ? styles.sectionArrowOpen : ''}`}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

/** 1 つの group をコラプス可能なセクションとして描画（picker 種別に応じて分岐、未指定は再帰 chip） */
function GroupSection({
  group,
  value,
  onSelect,
  depth,
  recommendedKeys,
  optionIcon,
}: {
  group: HierarchicalOption;
  value: string;
  onSelect: (key: string) => void;
  depth: number;
  recommendedKeys?: RecommendedKeys;
  optionIcon?: (option: HierarchicalOption) => string | undefined;
}): ReactNode {
  const [collapsed, setCollapsed] = useState(false);
  const children = group.children ?? [];
  const picker = metaString(group, 'picker');
  const hasSelected = groupContainsValue(group, value);

  // 配置グリッド/カメラアングル図/被写体サイズ図はコラプス見出しを出さず、図を直接表示する
  if (picker === 'placement_grid' || picker === 'camera_angle' || picker === 'shot_size') {
    const pickerBody =
      picker === 'placement_grid' ? (
        <PlacementGrid options={children} value={value} onSelect={onSelect} />
      ) : picker === 'camera_angle' ? (
        <CameraAnglePicker options={children} value={value} onSelect={onSelect} />
      ) : (
        <ShotSizePicker options={children} value={value} onSelect={onSelect} />
      );
    return (
      <section className={styles.section}>
        <div className={styles.sectionBody}>{pickerBody}</div>
      </section>
    );
  }

  const leaves = children.filter((c) => !c.children || c.children.length === 0);
  const groups = children.filter((c) => c.children && c.children.length > 0);
  return (
    <section className={styles.section}>
      <button
        type="button"
        className={`${styles.sectionHeaderBtn} ${depth === 0 ? styles.sectionHeaderTop : styles.sectionHeaderSub} ${hasSelected ? styles.sectionHeaderActive : ''}`}
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
      >
        <span className={depth === 0 ? styles.sectionHeading : styles.subHeading}>{group.label}</span>
        <Chevron open={!collapsed} />
      </button>
      {!collapsed && (
        <div className={styles.sectionBody}>
          {leaves.length > 0 && (
            <OptionChips options={leaves} value={value} onSelect={onSelect} recommendedKeys={recommendedKeys} optionIcon={optionIcon} />
          )}
          {groups.map((g) => (
            <GroupSection key={g.id} group={g} value={value} onSelect={onSelect} depth={depth + 1} recommendedKeys={recommendedKeys} optionIcon={optionIcon} />
          ))}
        </div>
      )}
    </section>
  );
}

export function AttributeSelectModal({
  label,
  options,
  value,
  onChange,
  isOpen,
  onClose,
  noneLabel = '指定なし',
  defaultValue = 'none',
  recommendedKeys,
  optionIcon,
}: AttributeSelectModalProps) {
  // Escape で閉じる + 背面スクロールロック
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSelect = (key: string) => {
    onChange(key);
    onClose();
  };

  const hasValue = value && value !== 'none' && value !== defaultValue;

  // トップレベルが group / leaf 混在の場合に対応
  const topLeaves = options.filter((o) => !o.children || o.children.length === 0);
  const topGroups = options.filter((o) => o.children && o.children.length > 0);

  // 単一の配置グリッド/カメラアングル図のみのモーダルは、コンテンツにフィットさせる。
  // width:auto は子の % 幅が解決できず潰れるため、種別ごとに max-width で絞る。
  const singlePicker =
    topLeaves.length === 0 && topGroups.length === 1 ? (metaString(topGroups[0], 'picker') ?? '') : '';
  const isPickerModal =
    singlePicker === 'placement_grid' ||
    singlePicker === 'camera_angle' ||
    singlePicker === 'shot_size';
  const pickerMaxWidth =
    singlePicker === 'placement_grid'
      ? 'min(500px, 100%)'
      : singlePicker === 'shot_size'
        ? 'min(360px, 100%)'
        : 'min(380px, 100%)';

  return createPortal(
    // 素材モーダルと共通の modal-common.css 基盤(オーバーレイ/サーフェス/ヘッダー)を使用
    <div className="modal-overlay" onMouseDown={onClose} role="dialog" aria-modal="true" aria-label={label}>
      <div
        className={isPickerModal ? 'modal-card' : `modal-card modal-card--lg ${styles.modalCard}`}
        style={isPickerModal ? { maxWidth: pickerMaxWidth } : undefined}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="modal-header">
          <h2 className="modal-title">{label}</h2>
          <div className={styles.headerActions}>
            {hasValue && (
              <button type="button" className={styles.resetButton} onClick={() => handleSelect('none')}>
                {noneLabel}
              </button>
            )}
            <button type="button" className="modal-close-button" onClick={onClose} aria-label="閉じる">
              ✕
            </button>
          </div>
        </header>
        <div className="modal-body">
          {topLeaves.length > 0 && (
            <section className={styles.section}>
              <OptionChips options={topLeaves} value={value} onSelect={handleSelect} recommendedKeys={recommendedKeys} optionIcon={optionIcon} />
            </section>
          )}
          {topGroups.map((g) => (
            <GroupSection key={g.id} group={g} value={value} onSelect={handleSelect} depth={0} recommendedKeys={recommendedKeys} optionIcon={optionIcon} />
          ))}
        </div>
      </div>
    </div>,
    document.body,
  );
}
